import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Archive, Calendar, ClipboardList, Loader2, Trash2, FileText, FileSpreadsheet, FileDown } from "lucide-react";
import { toast } from "sonner";
import { laporanKegiatanAPI } from "../lib/api";
import { exportToExcel, exportToPDF, exportToWord } from "../lib/exportUtils";
import { useApp } from "../context/AppContext";

const KATEGORI = { satpam: "Satpam", tukang_taman: "Tukang Taman", petugas_sampah: "Petugas Sampah" };
const KAT_COLOR = {
  satpam: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  tukang_taman: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  petugas_sampah: "bg-amber-100 text-amber-800 hover:bg-amber-100",
};
const BULAN_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const HARI_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const formatFullDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${HARI_ID[d.getDay()]}, ${d.getDate()} ${BULAN_ID[d.getMonth()]} ${d.getFullYear()}`;
};

const ArsipLaporan = () => {
  const { user } = useApp();
  const canDelete = ["admin", "super_admin"].includes(user?.role);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [tanggal, setTanggal] = useState(todayISO);
  const [kategori, setKategori] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    laporanKegiatanAPI.list(tanggal ? { tanggal } : {})
      .then((d) => setRows(kategori === "all" ? d : d.filter((r) => r.kategori === kategori)))
      .catch(() => toast.error("Gagal memuat arsip"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tanggal, kategori]);

  const grouped = useMemo(() => {
    const by = { satpam: [], tukang_taman: [], petugas_sampah: [] };
    rows.forEach((r) => { if (by[r.kategori]) by[r.kategori].push(r); });
    return by;
  }, [rows]);

  const remove = async (id) => {
    if (!window.confirm("Hapus laporan ini secara permanen?")) return;
    try { await laporanKegiatanAPI.remove(id); toast.success("Terhapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const shiftDay = (delta) => {
    const d = new Date(tanggal + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setTanggal(d.toISOString().slice(0, 10));
  };

  const doExport = (kind) => {
    if (!rows.length) { toast.error("Tidak ada laporan untuk tanggal ini"); return; }
    const headers = ["Kategori", "Petugas", "Jam", "Deskripsi", "Jumlah Foto"];
    const data = rows.map((r) => [KATEGORI[r.kategori] || r.kategori, r.petugas_nama, r.jam || "-", r.deskripsi, String((r.foto || []).length)]);
    const summary = [
      ["Tanggal", formatFullDate(tanggal)],
      ["Total Laporan", String(rows.length)],
      ["Satpam", String(grouped.satpam.length)],
      ["Tukang Taman", String(grouped.tukang_taman.length)],
      ["Petugas Sampah", String(grouped.petugas_sampah.length)],
    ];
    const title = `Arsip Laporan Kegiatan — ${formatFullDate(tanggal)}`;
    const subtitle = "Warga DM Residence Garut";
    const fn = `arsip-laporan-${tanggal}`;
    if (kind === "xlsx") exportToExcel({ filename: `${fn}.xlsx`, sheetName: "Arsip Laporan", headers, rows: data, summaryRows: summary });
    else if (kind === "pdf") exportToPDF({ filename: `${fn}.pdf`, title, subtitle, headers, rows: data, summary });
    else exportToWord({ filename: `${fn}.doc`, title, subtitle, headers, rows: data, summary });
    toast.success(`${kind.toUpperCase()} diunduh`);
  };

  return (
    <div className="space-y-4">
      <Card className="border-emerald-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center"><Archive className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>Arsip Laporan Kegiatan</h2>
              <p className="text-xs text-slate-500">Telusuri riwayat laporan Satpam, Tukang Taman & Petugas Sampah per tanggal</p>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="rounded-xl border bg-slate-50 p-3 flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Tanggal</Label>
              <div className="flex gap-1 mt-1">
                <Button size="sm" variant="outline" onClick={() => shiftDay(-1)} data-testid="arsip-prev-day">‹</Button>
                <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="w-40" data-testid="arsip-tanggal-input" />
                <Button size="sm" variant="outline" onClick={() => shiftDay(1)} data-testid="arsip-next-day">›</Button>
                {tanggal !== todayISO && <Button size="sm" variant="outline" onClick={() => setTanggal(todayISO)} data-testid="arsip-today-btn"><Calendar className="h-3 w-3 mr-1" />Hari Ini</Button>}
              </div>
            </div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={kategori} onValueChange={setKategori}>
                <SelectTrigger className="w-44 mt-1" data-testid="arsip-kategori-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  <SelectItem value="satpam">Satpam</SelectItem>
                  <SelectItem value="tukang_taman">Tukang Taman</SelectItem>
                  <SelectItem value="petugas_sampah">Petugas Sampah</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1.5 ml-auto items-center" data-testid="arsip-export-bar">
              <span className="text-xs font-bold text-slate-700 hidden sm:inline">Unduh:</span>
              <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="arsip-export-pdf"><FileText className="h-3.5 w-3.5 mr-1 text-rose-600" />PDF</Button>
              <Button variant="outline" size="sm" onClick={() => doExport("docx")} data-testid="arsip-export-docx"><FileDown className="h-3.5 w-3.5 mr-1 text-blue-600" />Word</Button>
              <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="arsip-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-emerald-600" />Excel</Button>
            </div>
          </div>

          {/* Ringkasan */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="arsip-summary">
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Total Laporan</div>
              <div className="text-2xl font-black text-emerald-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{rows.length}</div>
            </div>
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-blue-700 tracking-wider">Satpam</div>
              <div className="text-2xl font-black text-blue-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{grouped.satpam.length}</div>
            </div>
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Tukang Taman</div>
              <div className="text-2xl font-black text-emerald-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{grouped.tukang_taman.length}</div>
            </div>
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Petugas Sampah</div>
              <div className="text-2xl font-black text-amber-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{grouped.petugas_sampah.length}</div>
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-1" />Memuat arsip...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
              <ClipboardList className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <div className="text-sm text-slate-500">Tidak ada laporan pada {formatFullDate(tanggal)}{kategori !== "all" ? ` untuk kategori ${KATEGORI[kategori]}` : ""}.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="arsip-list">
              {rows.map((r) => (
                <div key={r.id} className="rounded-xl border bg-white p-4 flex gap-3" data-testid={`arsip-item-${r.id}`}>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] ${KAT_COLOR[r.kategori]}`}>{KATEGORI[r.kategori]}</Badge>
                      {r.jam && <span className="text-[11px] text-slate-500 font-mono">{r.jam} WIB</span>}
                      <span className="text-[11px] text-slate-400">•</span>
                      <span className="font-semibold text-sm text-slate-800">{r.petugas_nama}</span>
                      {r.created_by && <span className="text-[11px] text-slate-400 ml-auto">oleh {r.created_by}</span>}
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-line">{r.deskripsi}</div>
                    {r.foto?.length > 0 && (
                      <div className="flex gap-2 mt-2 overflow-x-auto">
                        {r.foto.map((f, i) => (
                          <a key={i} href={f} target="_blank" rel="noreferrer" className="shrink-0">
                            <img src={f} alt="" className="h-20 w-20 rounded-lg object-cover border shadow-sm hover:scale-105 transition" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {canDelete && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 shrink-0" onClick={() => remove(r.id)} data-testid={`arsip-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ArsipLaporan;
